import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";

import Avatar from "../../components/Avatar";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  useGetAcademyQuery,
  useGetDepartmentQuery,
  useGetDivisionsQuery,
  useGetUsersQuery,
  useGetUserByEmailQuery,
} from "../../slices/userApiSlice";
import {
  useGetMaintenanceByTechnicianEmailQuery,
  useGetSchedulesByTechnicianEmailQuery,
} from "../../slices/maintenanceApiSlice";

import "./css/TabSwitcher.css";
import "./css/table.css";

const ROWS_PER_PAGE = 10;
const USER_TABS = {
  SUPERVISOR: "Supervisor",
  TECHNICIAN: "Technician",
};

const WorkAssigned = ({ email }) => {
  const { data: maintenanceData, isLoading: maintenanceLoading } =
    useGetMaintenanceByTechnicianEmailQuery(email);
  const { data: repairData, isLoading: repairLoading } =
    useGetSchedulesByTechnicianEmailQuery(email);

  if (maintenanceLoading || repairLoading) {
    return <span>Loading...</span>;
  }

  const maintenanceCount = Array.isArray(maintenanceData)
    ? maintenanceData.length
    : 0;
  const repairCount = Array.isArray(repairData) ? repairData.length : 0;
  const totalWork = maintenanceCount + repairCount;

  return (
    <Badge variant="secondary" className="font-normal">
      {totalWork}
    </Badge>
  );
};

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

const Users = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState(USER_TABS.TECHNICIAN);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      if (tabParam.toLowerCase() === "supervisor") {
        setActiveTab(USER_TABS.SUPERVISOR);
      } else if (tabParam.toLowerCase() === "technician") {
        setActiveTab(USER_TABS.TECHNICIAN);
      }
    }
  }, [searchParams]);
  const [selectedDivisionId, setSelectedDivisionId] = useState("all");
  const [data, setData] = useState([]);
  const [sortOrder, setSortOrder] = useState({ column: null, ascending: true });

  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email);

  const academyId = userByEmial?.user?.academyId;

  const { data: academies, isLoading: academiesLoading } = useGetAcademyQuery();
  const { data: department, isLoading: departmentLoading } =
    useGetDepartmentQuery();
  const { data: divisions, isLoading: divisionsLoading } =
    useGetDivisionsQuery();
  const { data: users, isLoading: usersLoading } = useGetUsersQuery();

  const isLoading =
    academiesLoading || departmentLoading || divisionsLoading || usersLoading;

  const getAcademyName = useCallback(
    (academyId) => {
      const academy = academies?.find((a) => a.academyId === academyId);
      return academy ? academy.name : "Unknown Academy";
    },
    [academies],
  );

  const getDivisionName = useCallback(
    (divisionID) => {
      if (!divisionID || divisionID === "all" || !divisions?.length)
        return "Division";
      const division = divisions.find(
        (d) => d.divisionId === divisionID || d._id === divisionID,
      );
      return division ? division.name : "Division";
    },
    [divisions],
  );

  // Prepare division options for CustomSelect
  const divisionOptions = useMemo(() => {
    return (
      divisions?.map((division) => ({
        value: division.divisionId || division._id,
        label: division.name,
        divisionId: division.divisionId || division._id,
        name: division.name,
      })) || []
    );
  }, [divisions]);

  const getDepartmentName = useCallback(
    (departmentId) => {
      const departmentItem = department?.find(
        (d) => d.departmentId === departmentId,
      );
      return departmentItem ? departmentItem.name : "Unknown Department";
    },
    [department],
  );

  useEffect(() => {
    if (
      !users ||
      !userByEmial?.user?.academyId ||
      !userByEmial?.user?.departmentId
    ) {
      return;
    }

    const loginAcademyId = userByEmial.user.academyId?.trim().toLowerCase();
    const loginDepartmentId = userByEmial.user.departmentId
      ?.trim()
      .toLowerCase();

    const filtered = users.filter((user) => {
      const userAcademyId = user.academyId?.trim().toLowerCase();
      const userDepartmentId = user.departmentId?.trim().toLowerCase();
      const userRole = user.role?.name?.toLowerCase();

      return (
        userAcademyId === loginAcademyId &&
        userDepartmentId === loginDepartmentId &&
        userRole !== "manager"
      );
    });

    setData(filtered);
  }, [users, userByEmial]);

  const filteredData = useMemo(() => {
    return (data || []).filter((item) => {
      const matchesTab =
        item.role?.name.toLowerCase() === activeTab.toLowerCase();
      const matchesSearch = Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      );
      const matchesDivision =
        selectedDivisionId === "all" || item.divisionId === selectedDivisionId;
      return matchesTab && matchesSearch && matchesDivision;
    });
  }, [data, activeTab, searchTerm, selectedDivisionId]);

  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);

  const displayedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage]);

  const sortData = useCallback(
    (column, ascending) => {
      const sortedData = [...data].sort((a, b) => {
        const valueA = a[column];
        const valueB = b[column];

        if (valueA < valueB) return ascending ? -1 : 1;
        if (valueA > valueB) return ascending ? 1 : -1;
        return 0;
      });
      setData(sortedData);
    },
    [data],
  );

  const handleSort = useCallback(
    (column) => {
      const newSortOrder =
        column === sortOrder.column ? !sortOrder.ascending : true;

      setSortOrder({
        column,
        ascending: newSortOrder,
      });
      sortData(column, newSortOrder);
    },
    [sortOrder, sortData],
  );

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedDivisionId("all"); // Reset division filter when changing tabs
    setSearchParams({ tab: tab.toLowerCase() }); // Update the URL
  }, [setSearchParams]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading users...", { id: "loading-users" });
    } else {
      toast.dismiss("loading-users");
    }
  }, [isLoading]);

  // Handler functions

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold tracking-tight">{activeTab} Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and organize {activeTab.toLowerCase()}s across different divisions
          </p>
        </div>

      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:w-auto">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>
            <div className="w-full sm:w-[200px]">
              <CustomSelect
                options={divisionOptions}
                value={selectedDivisionId}
                onChange={setSelectedDivisionId}
                placeholder="Division"
                allOptionLabel="All Divisions"
                getDisplayValue={getDivisionName}
                allValue="all"
                minWidth={180}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold">User</TableHead>
                  <TableHead className="font-semibold">Division</TableHead>
                  {activeTab === USER_TABS.TECHNICIAN && (
                    <TableHead className="font-semibold">
                      Work Assigned
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={activeTab === USER_TABS.TECHNICIAN ? 3 : 2}
                      className="text-center py-12 text-muted-foreground"
                    >
                      No {activeTab.toLowerCase()}s found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedData.map((item, index) => (
                    <TableRow key={item._id || index}>
                      <TableCell>
                        <div
                          className="flex items-center gap-3 cursor-pointer group"
                          onClick={() =>
                            navigate(`/manager/users/${item.email}`)
                          }
                        >
                          <Avatar user={item} size={40} />
                          <div>
                            <div className="font-medium group-hover:text-primary transition-colors">
                              {item.name}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {item.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getDivisionName(item.divisionId)}
                        </Badge>
                      </TableCell>
                      {activeTab === USER_TABS.TECHNICIAN && (
                        <TableCell>
                          <WorkAssigned email={item.email} />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
